
import { defineComponent, ref, watch, reactive, toRefs, PropType, nextTick } from 'vue'
import { isFileType } from '@/utils/file'
import _ from 'lodash'

function capabilities () {
  const e = document.createElement('div')
  return {
    drop: (('draggable' in e) || ('ondragstart' in e && 'ondrop' in e)),
    reader: 'FormData' in window && 'FileReader' in window
  }
}

export default defineComponent({
  name: 'ImageDisplay',
  props: {
    title: {
      type: String
    },
    id: {
      type: String,
      default: Date.now().toString(32)
    },
    file: {
      type: File,
      default: null
    },
    inputChange: {
      type: Function,
      default: (e: any) => {
        console.log(e)
      }
    },
    label: {
      type: String,
      default: 'Drop a file '
    },
    accepts: {
      type: Array as PropType<Array<string>>,
      default: () => ['.jpg', '.jpeg', '.png']
    },
    max: {
      type: Number,
      default: 1
    },
    isImage: {
      type: Boolean,
      default: false
    },
    hasIcon: {
      type: Boolean,
      default: false
    },
    preParse: Function,
    postParse: Function
  },
  emits: ['change', 'drop'],
  setup (props, { emit }) {
    const state = reactive({
      loading: false,
      importDone: false,
      importData: {
        raw: null,
        workbook: null,
        worksheet: null
      },
      productImageFile: ref(),
      fileList: ref([] as any[]),
      fileInput: ref()
    })

    watch(() => state.fileList, () => {
      emit('change', { files: state.fileList })
    }, { deep: true })

    watch(() => state.productImageFile, () => {
      emit('change', state.productImageFile)
    })

    async function previewImage () {
      await nextTick()
      if (state?.fileList[0]) {
        const el = document.querySelector('.productImage')
        if (el) {
          el?.setAttribute('src', URL.createObjectURL(state.fileList[0]))
        }
      }
    }

    function onChange (event) {
      const files = event.target.files
      if (!files?.length) return

      // Input handles checks itself so we can skip those
      assert(event)
    }

    function assert (event) {
      const files: Array<File> = event.type === 'change' ? event.srcElement.files : event.dataTransfer.files
      if (files.length > props.max) {
        console.error(`Only supports uploading of ${ props.max } files!`)
        if (!event.currentTarget.classList.contains('file-drop--error')) {
          event.currentTarget.classList.add('file-drop--error')
        }
        return
      }

      // console.log('Files list: ', files)
      files.forEach(rawFile => {
        // const rawFile = files[0]
        if (!isFileType(rawFile, props.accepts)) {
          console.error(`Only supports upload for ${ props.accepts }`)
          if (!event.currentTarget.classList.contains('file-drop--error')) {
            event.currentTarget.classList.add('file-drop--error')
          }
          return false
        }

        parse(rawFile)
      })
    }

    function remove () {
      state.fileList = []
      state.importDone = false
    }

    // function commitData ({ raw, workbook, worksheet, header, results, csvString }) {
    //   state.importData = { raw, workbook, worksheet }
    // }

    function parse (file: File, i = 0) {
      state.fileList[i] = file

      if (!props.preParse) {
        parseFile(file)
        return
      }

      if (props.preParse(file)) {
        parseFile(file)
      }
    }

    function parseFile (file: File) {
      state.loading = true

      if (file.type === 'image/jpg' || file.type === 'image/jpeg' || file.type === 'image/png') {
        const reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onload = function () {
          state.productImageFile = { fileName: file.name, file: reader.result as string }
          void previewImage()
        }

        reader.onerror = e => {
          console.log('Error parsing image file', e)
        }
        return
      }
    }

    function dragover (event) {
      event.preventDefault()
      if (event.currentTarget.classList.contains('file-drop--error')) event.currentTarget.classList.remove('file-drop--error')
      if (!event.currentTarget.classList.contains('file-drop--hover')) event.currentTarget.classList.add('file-drop--hover')
    }

    function dragleave (event) {
      event.currentTarget.classList.remove('file-drop--hover')
    }

    function drop (event) {
      event.stopPropagation()
      event.preventDefault()

      assert(event)
    }

    return {
      ...toRefs(state),
      onChange,
      remove,
      dragover,
      dragleave,
      drop
    }
  }
})
